import EmptyState from "@/components/EmptyState";
import Table, {
  TableFooter,
  TableControls,
  buildCopyTextColumn,
  buildActionColumn,
  buildExpandableCountColumn,
  buildTagColumn
} from "@/components/Table";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Path } from "@/router";
import { useParams, useSearchWithParams } from "@/hooks/useSearchParams";
import SearchInput from "@/components/SearchInput";
import FacilitySideSheet from "@/features/facility/components/FacilitySideSheet";
import NotesTable from "@/features/global/components/NotesTable";
import AddEditNoteSideSheet from "@/features/global/components/AddEditNoteSideSheet";
import useCapabilities, {
  DOMAIN,
  PERMISSION,
  useCanManageFacilities,
  useHasCapabilityBool
} from "@/hooks/useCapabilities";
import { NoteNotableValues } from "@/types";
import { EditGovernanceRoleModal } from "@/pages/facilities/[facilityId]/people/governance/_EditGovernanceRoleModal";
import { ArchiveGovernanceRoleDialog } from "@/pages/facilities/[facilityId]/people/governance/_ArchiveGovernanceRoleDialog";
import { useGovernanceRoleArchival } from "@/pages/facilities/[facilityId]/people/governance/useGovernanceRoleArchival";
import { PersonAddGovernanceRoleModal } from "./_PersonAddGovernanceRoleModal";
import { PERSON_FACILITY_GOVERNANCE_ROLES_QUERY } from "./queries";
import {
  formatSectionDisplayName,
  getGovernanceStatusLabel,
  getGovernanceStatusTagType,
  getGovernanceStatusTagStyle,
  mapGovernanceStatus,
  matchesPersonGovernanceSearch,
  getEffectiveGovernanceSearch,
  clearGovernanceFilters
} from "./_utils";
import type { PersonGovernanceRole } from "./types";
import { GovernanceStatus } from "./types";

export function PersonGovernanceTable({ pgaId }: { pgaId: string }) {
  const canViewPersonCareer = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonCareer
  });
  const { params, addSearchParam, deleteSearchParams } = useParams();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search"
  });
  const urlShowArchived = params.showArchived === "true";
  const [showArchived, setShowArchivedState] = useState(urlShowArchived);

  const setShowArchived = (value: boolean) => {
    setShowArchivedState(value);
    if (value) {
      addSearchParam("showArchived", "true");
    } else {
      deleteSearchParams("showArchived");
    }
  };

  const { data, loading } = useQuery(PERSON_FACILITY_GOVERNANCE_ROLES_QUERY, {
    variables: { pgaId },
    skip: !canViewPersonCareer || !pgaId
  });

  const { archiveRole, unarchiveRole, archiving } = useGovernanceRoleArchival(PERSON_FACILITY_GOVERNANCE_ROLES_QUERY);

  const navigate = useNavigate();
  const location = useLocation();
  const { id: currentUserId } = useCapabilities();
  const canManageFacilities = useCanManageFacilities();
  const canManageGovernance = canManageFacilities;
  const { Menu, setMenu } = useTableRowMenu();
  const [isFacilitySideSheetOpen, setIsFacilitySideSheetOpen] = useState(false);
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | undefined>(undefined);
  const [roleToEdit, setRoleToEdit] = useState<PersonGovernanceRole | null>(null);
  const [roleToArchive, setRoleToArchive] = useState<PersonGovernanceRole | null>(null);
  const [roleToAddNote, setRoleToAddNote] = useState<PersonGovernanceRole | null>(null);
  const [isAddGovernanceRoleModalOpen, setIsAddGovernanceRoleModalOpen] = useState(false);

  const allRoles = useMemo<PersonGovernanceRole[]>(
    () =>
      (data?.person.facilityGovernanceRoles ?? []).map((node) => ({
        ...node,
        status: mapGovernanceStatus(node.status)
      })),
    [data]
  );

  const wasAllArchived = useRef<boolean | null>(null);
  useEffect(() => {
    if (loading || !data) {
      return;
    }

    const allArchived = allRoles.length > 0 && allRoles.every((role) => role.status === GovernanceStatus.ARCHIVED);
    const previouslyAllArchived = wasAllArchived.current;
    wasAllArchived.current = allArchived;

    if (allArchived && previouslyAllArchived !== true) {
      setShowArchivedState(true);
    }
  }, [loading, data, allRoles]);

  const effectiveSearch = getEffectiveGovernanceSearch(searchValue, debouncedSearch, params.search);

  const roles = useMemo(
    () =>
      allRoles
        .filter((role) => showArchived || role.status === GovernanceStatus.ACTIVE)
        .filter((role) => matchesPersonGovernanceSearch(role, effectiveSearch)),
    [allRoles, showArchived, effectiveSearch]
  );

  const personId = data?.person?.id ?? "";

  const activeFilterCount = [showArchived, effectiveSearch].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0 || roles.length < allRoles.length;

  function handleClearAllFilters() {
    clearGovernanceFilters({
      setShowArchived: setShowArchivedState,
      setSearchValue,
      deleteSearchParams
    });
  }

  async function handleArchiveGovernanceRole() {
    if (!roleToArchive) {
      return;
    }

    const success = await archiveRole(roleToArchive.id);
    if (success) {
      setRoleToArchive(null);
    }
  }

  async function handleUnarchiveGovernanceRole(role: PersonGovernanceRole) {
    await unarchiveRole(role.id);
  }

  function buildFacilityGovernancePath(facilityId: string): Path {
    const returnTo = location.pathname;
    return `/facilities/${facilityId}/people/governance?return_to=${encodeURIComponent(returnTo)}` as Path;
  }

  const columns = [
    buildCopyTextColumn<PersonGovernanceRole>({
      accessorKey: "facility.name",
      header: "Facility Name",
      copyFieldLabel: "Facility Name",
      size: 250,
      enableSorting: false,
      getText: (row) => row.facility.name,
      getSecondaryText: (row) => formatSectionDisplayName(row.facility.section?.name),
      shouldShowCopyButton: (row) => !!row.facility.name,
      meta: { headerClassName: "hide-header-in-column" }
    }),
    {
      accessorFn: (row: PersonGovernanceRole) => row.title.title,
      header: "Role",
      id: "role",
      enableSorting: false
    },
    buildExpandableCountColumn<PersonGovernanceRole>({
      accessorKey: "notes",
      header: "Notes",
      getCount: (row) => row.notes.length,
      size: 100,
      minSize: 80,
      showExpandIcon: true
    }),
    buildTagColumn<PersonGovernanceRole>({
      accessorKey: "status",
      header: "Status",
      getTagLabel: (row) => getGovernanceStatusLabel(row.status),
      getTagType: (row) => getGovernanceStatusTagType(row.status),
      getTagStyle: (row) => getGovernanceStatusTagStyle(row.status),
      meta: {
        headerClassName: "hide-header-in-column"
      }
    }),
    buildActionColumn<PersonGovernanceRole>({
      getActions: (row) => {
        const actions: Action[] = [];

        if (canManageGovernance && row.status === GovernanceStatus.ACTIVE) {
          actions.push({
            text: "Edit Role",
            section: "Manage",
            onClick: () => setRoleToEdit(row)
          });
        }

        if (canManageGovernance && row.status === GovernanceStatus.ARCHIVED) {
          actions.push({
            text: "Unarchive Role",
            section: "Manage",
            iconName: "unarchive",
            onClick: () => handleUnarchiveGovernanceRole(row)
          });
        }

        actions.push({
          text: "View Facility",
          section: "Actions",
          onClick: () => {
            navigate(`/facilities/${row.facility.id}`);
          }
        });

        if (canManageGovernance) {
          actions.push({
            text: "Add Note",
            section: "Actions",
            onClick: () => setRoleToAddNote(row)
          });
        }

        if (canManageGovernance && row.status === GovernanceStatus.ACTIVE) {
          actions.push({
            text: "Archive Role",
            iconName: "archive",
            destructive: true,
            onClick: () => setRoleToArchive(row)
          });
        }

        return actions;
      },
      onViewClick: (row) => {
        setSelectedFacilityId(row.facility.id);
        setIsFacilitySideSheetOpen(true);
      },
      viewButtonTitle: "View facility details",
      actionId,
      setMenu,
      getRowId: (row) => row.id
    })
  ];

  return (
    <>
      <Menu />
      <div className="pt-12" />
      <Table
        data={roles}
        loading={loading}
        hasActiveFilters={hasActiveFilters}
        getRowCanExpand={(row) => row.original.notes.length > 0}
        onClickRow={(row) => {
          navigate(buildFacilityGovernancePath(row.original.facility.id));
        }}
        columns={columns}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            actions={
              canManageFacilities
                ? [
                    {
                      text: "Add Governance Role",
                      iconName: "add",
                      variant: "filled",
                      onClick: () => setIsAddGovernanceRoleModalOpen(true),
                      testId: "add-governance-role-button"
                    }
                  ]
                : []
            }
            filterChips={[
              {
                label: "Show Archived",
                onClick: () => setShowArchived(!showArchived),
                selected: showArchived
              }
            ]}
            activeFilters={activeFilterCount}
            onClearAllFilters={handleClearAllFilters}
            onSortClick={onSortClick}
          >
            <SearchInput
              data-name="search"
              placeholder="Search Governance Roles"
              query={searchValue}
              onChange={setSearchValue}
              onClear={clearSearch}
            />
          </TableControls>
        )}
        renderExpandedRow={({ row }) => (
          <NotesTable
            variant="details-panel"
            data={row.original.notes}
            title={`Notes (${row.original.notes.length})`}
          />
        )}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Governance Roles"
            caption="There are no governance roles associated with this person."
            iconName="person"
          />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Results Found" caption="Try a different search or filter" iconName="person" />
        )}
        renderFooter={() => <TableFooter totalRowCount={roles.length} />}
      />
      <FacilitySideSheet
        open={isFacilitySideSheetOpen}
        onClose={() => {
          setIsFacilitySideSheetOpen(false);
          setSelectedFacilityId(undefined);
        }}
        facilityId={selectedFacilityId ?? null}
      />
      <EditGovernanceRoleModal
        open={roleToEdit !== null}
        role={roleToEdit}
        facility={roleToEdit?.facility}
        onClose={() => setRoleToEdit(null)}
        refetchQueries={[PERSON_FACILITY_GOVERNANCE_ROLES_QUERY]}
      />
      {isAddGovernanceRoleModalOpen && (
        <PersonAddGovernanceRoleModal
          open
          personId={personId}
          onClose={() => setIsAddGovernanceRoleModalOpen(false)}
          refetchQueries={[PERSON_FACILITY_GOVERNANCE_ROLES_QUERY]}
        />
      )}
      <ArchiveGovernanceRoleDialog
        open={roleToArchive !== null}
        onClose={() => setRoleToArchive(null)}
        onConfirm={handleArchiveGovernanceRole}
        isLoading={archiving}
        facilityName={roleToArchive?.facility.name}
        roleLabel={roleToArchive?.title.title}
      />
      {roleToAddNote && (
        <AddEditNoteSideSheet
          open
          onClose={() => setRoleToAddNote(null)}
          notableId={roleToAddNote.id}
          notableType={NoteNotableValues.FACILITY_ASSOCIATION}
          refetchQueries={[PERSON_FACILITY_GOVERNANCE_ROLES_QUERY]}
          currentUserId={currentUserId || undefined}
          pgaId={pgaId}
          objectType="person"
          onSaveSuccess={() => setRoleToAddNote(null)}
        />
      )}
    </>
  );
}
